import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { 
  Navigation2, 
  Plus, 
  Trash2, 
  Edit3,
  MapPin,
  Eye,
  Camera,
  DoorOpen,
  TreePine,
  Home,
  Building2,
  Crosshair,
  Loader2,
  Link2,
  Package,
  AlertCircle,
  Shield,
  Copy,
  Search,
  ChevronUp,
  ChevronDown,
  GripVertical
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTour } from '@/contexts/TourContext';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icons mapping
const BUTTON_ICONS = {
  door: DoorOpen,
  outdoor: TreePine,
  home: Home,
  building: Building2,
  pin: MapPin,
  crosshair: Crosshair,
};

// Link type icons
const LINK_TYPE_ICONS = {
  equipment: Package,
  ticket: AlertCircle,
  calibration: Shield,
  general: MapPin,
};

export function ViewPositionManager({ 
  sweeps = [],
  currentSweep,
  cameraPosition,
  onNavigate,
  onNavigateToLocation,
  matterportTourStops = [],
  matterportLabels = [],
  tags = [],
  overlayButtonsEnabled = true,
  getZoom,
}) {
  const { token, isAuthenticated, canEdit, isViewer } = useAuth();
  const { currentTour } = useTour();
  const [navPoints, setNavPoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingButton, setEditingButton] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [equipment, setEquipment] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [calibrations, setCalibrations] = useState([]);
  const [newButton, setNewButton] = useState({
    label: '',
    icon: 'pin',
    description: '',
    sweepId: '',
    rotation: null,
    zoom: null,
    link_type: 'general',
    linked_equipment_id: '',
    linked_ticket_id: '',
    linked_calibration_id: '',
  });

  // Fetch nav points from backend
  const fetchNavPoints = useCallback(async () => {
    if (!token || !currentTour?.id) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/navpoints/tour/${currentTour.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNavPoints(data);
      }
    } catch (err) {
      console.error('Failed to fetch nav points:', err);
    } finally {
      setLoading(false);
    }
  }, [token, currentTour?.id]);

  // Fetch linkable items (equipment, tickets, calibrations)
  const fetchLinkableItems = useCallback(async () => {
    if (!token || !currentTour?.id) return;
    
    try {
      // Fetch equipment
      const equipRes = await fetch(`${API_URL}/api/equipment/tour/${currentTour.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (equipRes.ok) {
        const data = await equipRes.json();
        setEquipment(data);
      }
      
      // Fetch tickets
      const ticketRes = await fetch(`${API_URL}/api/tickets/tour/${currentTour.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (ticketRes.ok) {
        const data = await ticketRes.json();
        setTickets(data);
      }
      
      // Fetch calibrations
      const calRes = await fetch(`${API_URL}/api/calibrations/tour/${currentTour.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (calRes.ok) {
        const data = await calRes.json();
        setCalibrations(data);
      }
    } catch (err) {
      console.error('Failed to fetch linkable items:', err);
    }
  }, [token, currentTour?.id]);

  useEffect(() => {
    if (isAuthenticated && currentTour?.id) {
      fetchNavPoints();
      fetchLinkableItems();
    } else {
      setNavPoints([]);
      setEquipment([]);
      setTickets([]);
      setCalibrations([]);
    }
  }, [isAuthenticated, currentTour?.id, fetchNavPoints, fetchLinkableItems]);

  // Move a nav point up or down
  const moveNavPoint = async (index, direction) => {
    if (!token) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= navPoints.length) return;
    
    // Create new order
    const newOrder = [...navPoints];
    const [moved] = newOrder.splice(index, 1);
    newOrder.splice(newIndex, 0, moved);
    
    // Update local state immediately for responsiveness
    setNavPoints(newOrder);
    
    // Save to backend
    try {
      const res = await fetch(`${API_URL}/api/navpoints/reorder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          navpoint_ids: newOrder.map(np => np.id)
        })
      });
      
      if (!res.ok) {
        // Revert on error
        fetchNavPoints();
        console.error('Failed to save reorder');
      }
    } catch (err) {
      fetchNavPoints();
      console.error('Failed to reorder:', err);
    }
  };

  // Capture current position
  const captureCurrentPosition = async () => {
    if (!currentSweep?.id) {
      alert('Please navigate to a position in the 3D viewer first');
      return null;
    }
    // Get current zoom level
    let currentZoom = 1.0;
    if (getZoom) {
      try {
        currentZoom = await getZoom();
      } catch (e) {
        console.warn('Could not get zoom:', e);
      }
    }
    return {
      sweepId: currentSweep.id,
      rotation: cameraPosition?.rotation || null,
      zoom: currentZoom,
    };
  };

  // Handle creating new button with current position
  const handleCreateWithCurrentPosition = async () => {
    const pos = await captureCurrentPosition();
    if (pos) {
      setNewButton(prev => ({
        ...prev,
        sweepId: pos.sweepId,
        rotation: pos.rotation,
        zoom: pos.zoom,
      }));
    }
  };

  // Handle create button
  const handleCreate = async () => {
    if (!newButton.label || !currentTour?.id) return;
    
    setSaving(true);
    try {
      const navPointData = {
        tour_id: currentTour.id,
        label: newButton.label,
        icon: newButton.icon,
        description: newButton.description || null,
        style: 'primary',
        sweep_id: newButton.sweepId || null,
        rotation: newButton.rotation ? { x: newButton.rotation.x, y: newButton.rotation.y } : null,
        zoom: newButton.zoom || null,
        screen_position: { x: 50, y: 90 },
        link_type: newButton.link_type || 'general',
        linked_equipment_id: newButton.linked_equipment_id || null,
        linked_ticket_id: newButton.linked_ticket_id || null,
        linked_calibration_id: newButton.linked_calibration_id || null,
      };
      
      const res = await fetch(`${API_URL}/api/navpoints`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(navPointData)
      });

      if (res.ok) {
        const created = await res.json();
        setNavPoints(prev => [...prev, created]);
        setNewButton({ 
          label: '', 
          icon: 'pin', 
          description: '',
          sweepId: '', 
          rotation: null,
          zoom: null,
          link_type: 'general',
          linked_equipment_id: '',
          linked_ticket_id: '',
          linked_calibration_id: '',
        });
        setIsCreateDialogOpen(false);
      }
    } catch (err) {
      console.error('Failed to create nav point:', err);
    } finally {
      setSaving(false);
    }
  };

  // Handle update button
  const handleUpdate = async () => {
    if (!editingButton) return;
    
    setSaving(true);
    try {
      const updateData = {
        label: editingButton.label,
        icon: editingButton.icon,
        sweep_id: editingButton.sweep_id || null,
        rotation: editingButton.rotation ? { x: editingButton.rotation.x, y: editingButton.rotation.y } : null,
        zoom: editingButton.zoom || null,
      };

      const res = await fetch(`${API_URL}/api/navpoints/${editingButton.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      if (res.ok) {
        const updated = await res.json();
        setNavPoints(prev => prev.map(np => np.id === updated.id ? updated : np));
        setEditingButton(null);
      }
    } catch (err) {
      console.error('Failed to update nav point:', err);
    } finally {
      setSaving(false);
    }
  };

  // Handle delete button
  const handleDelete = async (navPointId) => {
    try {
      const res = await fetch(`${API_URL}/api/navpoints/${navPointId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        setNavPoints(prev => prev.filter(np => np.id !== navPointId));
      }
    } catch (err) {
      console.error('Failed to delete nav point:', err);
    }
  };

  // Handle capture for editing
  const handleCaptureForEdit = async () => {
    const pos = await captureCurrentPosition();
    if (pos && editingButton) {
      setEditingButton(prev => ({
        ...prev,
        sweep_id: pos.sweepId,
        rotation: pos.rotation,
        zoom: pos.zoom,
      }));
    }
  };

  // Navigate to button position
  const handleNavigate = (navPoint) => {
    console.log('handleNavigate called with navPoint:', navPoint);
    console.log('navPoint.sweep_id:', navPoint.sweep_id);
    console.log('navPoint.rotation:', navPoint.rotation);
    console.log('navPoint.zoom:', navPoint.zoom);
    if (navPoint.sweep_id && onNavigate) {
      console.log('Calling onNavigate with rotation and zoom:', navPoint.rotation, navPoint.zoom);
      onNavigate(navPoint.sweep_id, { 
        rotation: navPoint.rotation,
        zoom: navPoint.zoom 
      });
    }
  };

  const IconComponent = (iconName) => {
    return BUTTON_ICONS[iconName] || BUTTON_ICONS.pin;
  };

  // Combine all model locations for search (tour stops, mattertags, labels)
  const allModelLocations = [
    ...matterportTourStops.map(s => ({ ...s, type: 'tour', label: s.name })),
    ...tags.map(t => ({ ...t, type: 'mattertag', sweepId: t.sweepId })),
    ...matterportLabels.map(l => ({ ...l, type: 'label' })),
  ];

  // State for location search
  const [locationSearch, setLocationSearch] = React.useState('');
  
  // Filter locations based on search
  const filteredLocations = locationSearch.trim() 
    ? allModelLocations.filter(loc => 
        loc.label?.toLowerCase().includes(locationSearch.toLowerCase()) ||
        loc.name?.toLowerCase().includes(locationSearch.toLowerCase())
      )
    : [];

  // If not authenticated, show sign-in prompt
  if (!isAuthenticated) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Navigation2 className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">View Positions</span>
        </div>
        <div className="text-center py-6 text-muted-foreground">
          <p className="text-sm">Sign in to save view positions</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Navigation2 className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">View Positions</span>
          <Badge variant="secondary" className="text-[10px]">
            {navPoints.length}
          </Badge>
        </div>
        
        {canEdit && (
          <Button 
            variant="outline" 
            size="sm" 
            className="h-7 gap-1"
            onClick={() => setIsCreateDialogOpen(true)}
            disabled={!currentTour?.id}
          >
            <Plus className="h-3 w-3" />
            Add
          </Button>
        )}
      </div>

      {/* Model Locations Search */}
      {(matterportTourStops.length > 0 || tags.length > 0 || matterportLabels.length > 0) && (
        <div className="p-3 rounded-lg border bg-accent/30 space-y-2">
          <div className="flex items-center gap-2 text-xs font-medium">
            <Search className="h-3 w-3 text-primary" />
            Find Model Locations
            <Badge variant="outline" className="text-[9px]">
              {allModelLocations.length} found
            </Badge>
          </div>
          <Input
            placeholder="Search tour stops, tags, labels..."
            value={locationSearch}
            onChange={(e) => setLocationSearch(e.target.value)}
            className="h-8 text-xs"
          />
          {filteredLocations.length > 0 && (
            <div className="max-h-32 overflow-y-auto space-y-1">
              {filteredLocations.map((loc, idx) => (
                <button
                  key={`${loc.type}-${loc.id || idx}`}
                  onClick={() => {
                    console.log('Clicking location:', loc);
                    if (onNavigateToLocation) {
                      // Use the smart navigation function
                      onNavigateToLocation(loc);
                    } else if (loc.sweepId && onNavigate) {
                      // Fallback to sweep navigation
                      onNavigate(loc.sweepId);
                    }
                  }}
                  className="w-full flex items-center gap-2 p-2 rounded text-left text-xs hover:bg-secondary transition-colors border border-transparent hover:border-primary/20"
                >
                  <Badge variant="outline" className="text-[9px] shrink-0">
                    {loc.type}
                  </Badge>
                  <span className="truncate flex-1 font-medium">{loc.label || loc.name}</span>
                  <Eye className="h-3 w-3 text-primary shrink-0" />
                </button>
              ))}
            </div>
          )}
          {locationSearch && filteredLocations.length === 0 && (
            <p className="text-[10px] text-muted-foreground">No locations matching "{locationSearch}"</p>
          )}
        </div>
      )}

      {/* Viewer mode notice */}
      {isViewer && (
        <div className="text-xs text-muted-foreground bg-secondary/50 p-2 rounded">
          View-only access. Contact an admin to create or edit positions.
        </div>
      )}

      {/* Current Position Info - Shows where you are NOW */}
      <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs font-medium">
            <Camera className="h-3 w-3 text-primary" />
            Current Position
          </div>
          {currentSweep?.id && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs gap-1"
              onClick={() => {
                const info = `Sweep: ${currentSweep.id}\nRotation: X:${cameraPosition?.rotation?.x?.toFixed(2) || 0} Y:${cameraPosition?.rotation?.y?.toFixed(2) || 0}`;
                navigator.clipboard.writeText(info);
              }}
              title="Copy position info"
            >
              <Copy className="h-3 w-3" />
              Copy
            </Button>
          )}
        </div>
        {currentSweep?.id ? (
          <div className="text-xs space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground w-14">Sweep ID:</span>
              <code className="bg-muted px-1.5 py-0.5 rounded text-[10px] font-mono flex-1 truncate">
                {currentSweep.id}
              </code>
            </div>
            {cameraPosition?.rotation && (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground w-14">Rotation:</span>
                <span className="text-[10px]">
                  X: {cameraPosition.rotation.x?.toFixed(1)}° &nbsp; Y: {cameraPosition.rotation.y?.toFixed(1)}°
                </span>
              </div>
            )}
            {currentSweep.floor !== undefined && (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground w-14">Floor:</span>
                <span className="text-[10px]">{currentSweep.floor}</span>
              </div>
            )}
            <p className="text-[10px] text-muted-foreground pt-1 border-t border-primary/10 mt-2">
              💡 Navigate anywhere in the 3D view - this shows your current position. Click "Add" to save it.
            </p>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">Loading position data...</p>
        )}
      </div>

      {/* Nav Points List */}
      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : navPoints.length > 0 ? (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">Saved Positions:</p>
          {navPoints.map((navPoint, index) => {
            const Icon = IconComponent(navPoint.icon);
            return (
              <div
                key={navPoint.id}
                className="flex items-center gap-2 p-2 rounded-lg bg-secondary/50 hover:bg-secondary/80 transition-colors group"
              >
                {/* Reorder buttons - only show on hover for editors */}
                {canEdit && navPoints.length > 1 && (
                  <div className="flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button 
                      variant="ghost" 
                      size="icon-sm"
                      className="h-4 w-4 p-0"
                      onClick={() => moveNavPoint(index, 'up')}
                      disabled={index === 0}
                      title="Move up"
                    >
                      <ChevronUp className="h-3 w-3" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon-sm"
                      className="h-4 w-4 p-0"
                      onClick={() => moveNavPoint(index, 'down')}
                      disabled={index === navPoints.length - 1}
                      title="Move down"
                    >
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </div>
                )}
                
                <Icon className="h-4 w-4 text-primary flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{navPoint.label}</p>
                  {navPoint.sweep_id ? (
                    <p className="text-xs text-muted-foreground truncate">
                      {navPoint.rotation ? (
                        <>📍 Pose saved{navPoint.zoom && navPoint.zoom !== 1.0 ? ` + Zoom (${navPoint.zoom.toFixed(1)}x)` : ''}</>
                      ) : (
                        <>📍 Sweep only (default view)</>
                      )}
                    </p>
                  ) : (
                    <p className="text-xs text-yellow-500">⚠️ No position set</p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {navPoint.sweep_id && (
                    <Button 
                      variant="ghost" 
                      size="icon-sm"
                      onClick={() => handleNavigate(navPoint)}
                      title="Go to this view"
                    >
                      <Eye className="h-3 w-3" />
                    </Button>
                  )}
                  {canEdit && (
                    <>
                      <Button 
                        variant="outline" 
                        size="icon-sm"
                        onClick={() => setEditingButton({ ...navPoint })}
                        title="Edit button"
                      >
                        <Edit3 className="h-3 w-3" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon-sm"
                        onClick={() => handleDelete(navPoint.id)}
                        title="Delete button"
                      >
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-6">
          <Navigation2 className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No view positions saved</p>
          <p className="text-xs text-muted-foreground mt-1">
            Navigate to a view, then click Add to save it
          </p>
        </div>
      )}

      {/* All Sweeps (Admin only) */}
      {canEdit && sweeps.length > 0 && (
        <Collapsible>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full justify-between p-2 h-auto">
              <span className="text-xs font-medium text-muted-foreground">
                All Sweeps ({sweeps.length})
              </span>
              <ChevronDown className="h-3 w-3" />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="max-h-[300px] overflow-y-auto space-y-1 p-1">
              {sweeps.map((sweep, index) => (
                <div 
                  key={sweep.sid || index}
                  className={cn(
                    "flex items-center gap-2 p-2 rounded-md text-xs hover:bg-accent cursor-pointer",
                    currentSweep === sweep.sid && "bg-primary/10 border border-primary/30"
                  )}
                  onClick={() => onNavigate && onNavigate(sweep.sid)}
                >
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-muted text-muted-foreground font-medium flex-shrink-0">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-xs truncate">{sweep.sid || sweep.id || 'No ID'}</p>
                    {sweep.room && (
                      <p className="text-xs text-muted-foreground truncate">{sweep.room}</p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="h-6 w-6 flex-shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      const sweepId = sweep.sid || sweep.id || '';
                      if (sweepId) {
                        navigator.clipboard.writeText(sweepId);
                      }
                    }}
                    title="Copy Sweep ID"
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Status */}
      {!overlayButtonsEnabled && navPoints.length > 0 && (
        <p className="text-xs text-yellow-500 bg-yellow-500/10 p-2 rounded">
          ⚠️ Overlay buttons are hidden. Enable in Settings to see them on the viewer.
        </p>
      )}

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create View Position</DialogTitle>
            <DialogDescription>
              Save your current view position and optionally link it to an item.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Button Label *</Label>
              <Input
                placeholder="e.g., Tank Interior, Control Room..."
                value={newButton.label}
                onChange={(e) => setNewButton(prev => ({ ...prev, label: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Optional description for this location..."
                value={newButton.description}
                onChange={(e) => setNewButton(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Icon</Label>
              <Select 
                value={newButton.icon} 
                onValueChange={(value) => setNewButton(prev => ({ ...prev, icon: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="door">🚪 Door</SelectItem>
                  <SelectItem value="outdoor">🌲 Outdoor</SelectItem>
                  <SelectItem value="home">🏠 Home</SelectItem>
                  <SelectItem value="building">🏢 Building</SelectItem>
                  <SelectItem value="pin">📍 Pin</SelectItem>
                  <SelectItem value="crosshair">⊕ Crosshair</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>View Position</Label>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={handleCreateWithCurrentPosition}
              >
                <Crosshair className="h-4 w-4 mr-2" />
                Capture Current View
              </Button>
              {newButton.sweepId ? (
                <div className="text-xs p-2 bg-green-500/10 text-green-600 rounded">
                  ✓ Position captured: {newButton.sweepId.substring(0, 12)}...
                  {newButton.rotation && ' (with rotation)'}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Navigate to the exact view you want, then click capture.
                </p>
              )}
            </div>

            {/* Link to Item Section */}
            <div className="border-t pt-4 space-y-3">
              <div className="flex items-center gap-2">
                <Link2 className="h-4 w-4 text-muted-foreground" />
                <Label className="text-sm font-medium">Link to Item (Optional)</Label>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Link Type</Label>
                <Select 
                  value={newButton.link_type} 
                  onValueChange={(value) => setNewButton(prev => ({ 
                    ...prev, 
                    link_type: value,
                    linked_equipment_id: '',
                    linked_ticket_id: '',
                    linked_calibration_id: '',
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-3 w-3" />
                        General Location
                      </div>
                    </SelectItem>
                    <SelectItem value="equipment">
                      <div className="flex items-center gap-2">
                        <Package className="h-3 w-3" />
                        Link to Asset
                      </div>
                    </SelectItem>
                    <SelectItem value="ticket">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="h-3 w-3" />
                        Link to Active Issue
                      </div>
                    </SelectItem>
                    <SelectItem value="calibration">
                      <div className="flex items-center gap-2">
                        <Shield className="h-3 w-3" />
                        Link to Compliance Item
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Equipment selector */}
              {newButton.link_type === 'equipment' && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Select Asset</Label>
                  <Select 
                    value={newButton.linked_equipment_id} 
                    onValueChange={(value) => setNewButton(prev => ({ ...prev, linked_equipment_id: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose an asset..." />
                    </SelectTrigger>
                    <SelectContent>
                      {equipment.length === 0 ? (
                        <SelectItem value="" disabled>No assets found</SelectItem>
                      ) : (
                        equipment.map(eq => (
                          <SelectItem key={eq.id} value={eq.id}>
                            {eq.name} {eq.tag_number && `(${eq.tag_number})`}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Ticket selector */}
              {newButton.link_type === 'ticket' && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Select Active Issue</Label>
                  <Select 
                    value={newButton.linked_ticket_id} 
                    onValueChange={(value) => setNewButton(prev => ({ ...prev, linked_ticket_id: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose an issue..." />
                    </SelectTrigger>
                    <SelectContent>
                      {tickets.length === 0 ? (
                        <SelectItem value="" disabled>No issues found</SelectItem>
                      ) : (
                        tickets.map(t => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.title} - {t.status}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Calibration selector */}
              {newButton.link_type === 'calibration' && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Select Compliance Item</Label>
                  <Select 
                    value={newButton.linked_calibration_id} 
                    onValueChange={(value) => setNewButton(prev => ({ ...prev, linked_calibration_id: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a compliance item..." />
                    </SelectTrigger>
                    <SelectContent>
                      {calibrations.length === 0 ? (
                        <SelectItem value="" disabled>No compliance items found</SelectItem>
                      ) : (
                        calibrations.map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.equipment_name} - {c.status}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={!newButton.label || saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Position
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editingButton} onOpenChange={(open) => !open && setEditingButton(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit: {editingButton?.label}</DialogTitle>
            <DialogDescription>
              Update this button or set a new view position.
            </DialogDescription>
          </DialogHeader>
          
          {editingButton && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Button Label</Label>
                <Input
                  value={editingButton.label}
                  onChange={(e) => setEditingButton(prev => ({ ...prev, label: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label>Icon</Label>
                <Select 
                  value={editingButton.icon} 
                  onValueChange={(value) => setEditingButton(prev => ({ ...prev, icon: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="door">🚪 Door</SelectItem>
                    <SelectItem value="outdoor">🌲 Outdoor</SelectItem>
                    <SelectItem value="home">🏠 Home</SelectItem>
                    <SelectItem value="building">🏢 Building</SelectItem>
                    <SelectItem value="pin">📍 Pin</SelectItem>
                    <SelectItem value="crosshair">⊕ Crosshair</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>View Position</Label>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={handleCaptureForEdit}
                >
                  <Crosshair className="h-4 w-4 mr-2" />
                  Set to Current View
                </Button>
                {editingButton.sweep_id ? (
                  <div className="text-xs p-2 bg-green-500/10 text-green-600 rounded">
                    ✓ Position: {editingButton.sweep_id.substring(0, 12)}...
                    {editingButton.rotation && ' (with rotation)'}
                  </div>
                ) : (
                  <p className="text-xs text-yellow-500">
                    ⚠️ No position set - button will use default behavior
                  </p>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter className="flex justify-between">
            <Button 
              variant="destructive" 
              onClick={() => {
                if (editingButton) {
                  handleDelete(editingButton.id);
                  setEditingButton(null);
                }
              }}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditingButton(null)}>
                Cancel
              </Button>
              <Button onClick={handleUpdate} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ViewPositionManager;
